/**
 * API client for backend communication
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiError } from '../types';

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Dynamically determine the API URL based on current protocol
    const currentProtocol = window.location.protocol;
    const currentHost = window.location.hostname;
    
    // If we have an environment variable, use it but ensure it matches current protocol
    if (process.env.REACT_APP_API_URL) {
      let apiUrl = process.env.REACT_APP_API_URL;
      
      // If we're on HTTPS but API URL is HTTP, switch to HTTPS
      if (currentProtocol === 'https:' && apiUrl.startsWith('http://')) {
        apiUrl = apiUrl.replace('http://', 'https://');
      }
      // If we're on HTTP but API URL is HTTPS, switch to HTTP
      else if (currentProtocol === 'http:' && apiUrl.startsWith('https://')) {
        apiUrl = apiUrl.replace('https://', 'http://');
      }
      
      this.baseURL = `${apiUrl}/api/v1`;
    } else {
      // Fallback to relative path for proxy
      this.baseURL = '/api/v1';
    }
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const apiError: ApiError = {
          status: error.response?.status || 500,
          message: error.message || 'An unexpected error occurred',
        };

        if (error.response?.data) {
          const data = error.response.data as any;
          apiError.message = data.detail || data.message || apiError.message;
          apiError.details = data.errors || data.details;
          apiError.code = data.code;
        }

        // Handle authentication errors
        if (apiError.status === 401) {
          this.clearAuthToken();
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }

        return Promise.reject(apiError);
      }
    );
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  public setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  public clearAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  public isAuthenticated(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;

    // Basic token validation (check if it's not expired)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  // Generic HTTP methods
  async get<T = any>(url: string, params?: Record<string, any>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  // File upload
  async uploadFile<T = any>(url: string, formData: FormData): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;